// hooks/usePlusReturn.js
//
// Coming back from Stripe Checkout: ?plus=started | cancelled (plus
// &device=…, which useSelectedDevice consumes to select that monitor).
//
//   'waiting'   paid; the webhook may land a few seconds after Stripe sends
//               them back, so re-read the device every 2 s for ~30 s
//   'done'      Plus is on (clears itself after a few seconds)
//   'slow'      still not seen after ~30 s
//   'cancelled' they backed out of Checkout (clears itself)
//
// `reread(deviceId)` re-reads devices/{id} into the page's state and returns
// its data.

import { useEffect, useState } from 'react';

export default function usePlusReturn(deviceId, reread) {
  const [state, setState] = useState(() => {
    try {
      const v = new URLSearchParams(window.location.search).get('plus');
      return v === 'started' ? 'waiting' : v === 'cancelled' ? 'cancelled' : null;
    } catch { return null; }
  });

  // Drop ?plus so a reload or Back doesn't replay the message.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (!params.has('plus')) return;
      params.delete('plus');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    if (state !== 'waiting' || !deviceId) return undefined;
    let cancelled = false;
    let tries = 0;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const dev = await reread(deviceId);
        if (cancelled) return;
        const p = dev && dev.plus;
        if (p && (p.entitled === true || p.comped === true)) { setState('done'); return; }
      } catch (err) {
        console.error('usePlusReturn: re-read device', err);
      }
      if (tries >= 15) { setState('slow'); return; }
      timer = setTimeout(tick, 2000);
    };
    timer = setTimeout(tick, 1000);
    return () => { cancelled = true; clearTimeout(timer); };
    // reread is a stable page callback; deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, deviceId]);

  useEffect(() => {
    if (state !== 'done' && state !== 'cancelled') return undefined;
    const t = setTimeout(() => setState(null), 8000);
    return () => clearTimeout(t);
  }, [state]);

  return state;
}
